/**
 * usePerformanceMetrics Hook
 * 2026 Web Performance Standards
 *
 * Monitors Core Web Vitals including:
 * - INP (Interaction to Next Paint) - Target: <200ms
 * - LCP (Largest Contentful Paint) - Target: <2500ms
 * - CLS (Cumulative Layout Shift) - Target: <0.1
 * - FID (First Input Delay) - Target: <100ms
 *
 * @see https://web.dev/articles/inp
 */

import { useEffect, useRef, useCallback, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
  attribution?: string;
}

interface INPMetric extends PerformanceMetric {
  name: 'INP';
  interactionTarget?: string;
  interactionType?: string;
}

interface PerformanceMetrics {
  inp: INPMetric | null;
  lcp: PerformanceMetric | null;
  cls: PerformanceMetric | null;
  fid: PerformanceMetric | null;
  ttfb: PerformanceMetric | null;
}

interface UsePerformanceMetricsOptions {
  /** Enable console warnings for slow interactions */
  enableWarnings?: boolean;
  /** INP threshold for warnings (ms) */
  inpWarningThreshold?: number;
  /** Callback when metrics are collected */
  onMetricCollected?: (metric: PerformanceMetric) => void;
  /** Enable analytics reporting */
  enableAnalytics?: boolean;
}

interface UsePerformanceMetricsReturn {
  /** Current performance metrics */
  metrics: PerformanceMetrics;
  /** Whether browser supports performance APIs */
  isSupported: boolean;
  /** Manually mark an interaction start */
  markInteractionStart: (name: string) => void;
  /** Manually mark an interaction end and get duration */
  markInteractionEnd: (name: string) => number | null;
  /** Get a summary of current performance */
  getSummary: () => string;
}

// ============================================
// THRESHOLDS (per web.dev guidelines)
// ============================================

const THRESHOLDS = {
  INP: { good: 200, poor: 500 },
  LCP: { good: 2500, poor: 4000 },
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 },
  TTFB: { good: 800, poor: 1800 },
} as const;

// ============================================
// HELPERS
// ============================================

function getRating(
  value: number,
  thresholds: { good: number; poor: number }
): 'good' | 'needs-improvement' | 'poor' {
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.poor) return 'needs-improvement';
  return 'poor';
}

function getInteractionTarget(entry: PerformanceEventTiming): string {
  if (entry.target) {
    const element = entry.target as Element;
    const tag = element.tagName?.toLowerCase() || 'unknown';
    const id = element.id ? `#${element.id}` : '';
    const className = element.className
      ? `.${String(element.className).split(' ')[0]}`
      : '';
    return `${tag}${id}${className}`;
  }
  return 'unknown';
}

// Type augmentation for PerformanceEventTiming
interface PerformanceEventTiming extends PerformanceEntry {
  processingStart: number;
  processingEnd: number;
  duration: number;
  interactionId?: number;
  target?: EventTarget;
}

// Type augmentation for LayoutShift (not yet in all TS libs)
interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput: boolean;
  value: number;
}

// ============================================
// HOOK
// ============================================

/**
 * Hook for monitoring Core Web Vitals performance metrics
 *
 * @example
 * ```tsx
 * function App() {
 *   const { metrics, getSummary } = usePerformanceMetrics({
 *     enableWarnings: true,
 *     inpWarningThreshold: 200,
 *   });
 *
 *   useEffect(() => {
 *     if (metrics.inp?.rating === 'poor') {
 *       console.error('Poor INP detected!', metrics.inp);
 *     }
 *   }, [metrics.inp]);
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function usePerformanceMetrics(
  options: UsePerformanceMetricsOptions = {}
): UsePerformanceMetricsReturn {
  const {
    enableWarnings = process.env.NODE_ENV === 'development',
    inpWarningThreshold = 200,
    onMetricCollected,
    enableAnalytics = false,
  } = options;

  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    inp: null,
    lcp: null,
    cls: null,
    fid: null,
    ttfb: null,
  });

  const interactionMarks = useRef<Map<string, number>>(new Map());
  const worstInp = useRef<number>(0);
  const clsValue = useRef<number>(0);

  // Check for PerformanceObserver support
  const isSupported =
    typeof window !== 'undefined' &&
    typeof PerformanceObserver !== 'undefined' &&
    typeof PerformanceObserver.supportedEntryTypes !== 'undefined';

  // INP Observer
  useEffect(() => {
    if (!isSupported) return;

    // Check if 'event' entry type is supported (for INP)
    if (!PerformanceObserver.supportedEntryTypes.includes('event')) {
      if (enableWarnings) {
        if (import.meta.env.DEV) console.warn('[Performance] Event timing not supported - INP monitoring unavailable');
      }
      return;
    }

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const eventEntry = entry as PerformanceEventTiming;

        // Calculate INP: time from interaction to next paint
        const inp = eventEntry.processingEnd - eventEntry.startTime;

        // Track worst INP
        if (inp > worstInp.current) {
          worstInp.current = inp;

          const inpMetric: INPMetric = {
            name: 'INP',
            value: inp,
            rating: getRating(inp, THRESHOLDS.INP),
            timestamp: Date.now(),
            interactionTarget: getInteractionTarget(eventEntry),
            interactionType: eventEntry.name,
          };

          setMetrics((prev) => ({ ...prev, inp: inpMetric }));
          onMetricCollected?.(inpMetric);

          // Warn for slow interactions
          if (import.meta.env.DEV && enableWarnings && inp > inpWarningThreshold) {
            console.warn(
              `[INP] Slow interaction detected: ${inp.toFixed(0)}ms`,
              `\n  Target: ${inpMetric.interactionTarget}`,
              `\n  Type: ${inpMetric.interactionType}`,
              `\n  Rating: ${inpMetric.rating}`
            );
          }

          // Analytics reporting
          if (enableAnalytics && typeof window !== 'undefined') {
            // Send to analytics (placeholder - integrate with your analytics)
            window.dispatchEvent(
              new CustomEvent('performance-metric', {
                detail: inpMetric,
              })
            );
          }
        }
      }
    });

    observer.observe({
      type: 'event',
      buffered: true,
      durationThreshold: 40, // Only track interactions longer than 40ms
    } as PerformanceObserverInit);

    return () => observer.disconnect();
  }, [isSupported, enableWarnings, inpWarningThreshold, onMetricCollected, enableAnalytics]);

  // LCP Observer
  useEffect(() => {
    if (!isSupported) return;
    if (!PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint')) {
      return;
    }

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];

      if (lastEntry) {
        const lcpMetric: PerformanceMetric = {
          name: 'LCP',
          value: lastEntry.startTime,
          rating: getRating(lastEntry.startTime, THRESHOLDS.LCP),
          timestamp: Date.now(),
        };

        setMetrics((prev) => ({ ...prev, lcp: lcpMetric }));
        onMetricCollected?.(lcpMetric);
      }
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });

    return () => observer.disconnect();
  }, [isSupported, onMetricCollected]);

  // CLS Observer
  useEffect(() => {
    if (!isSupported) return;
    if (!PerformanceObserver.supportedEntryTypes.includes('layout-shift')) {
      return;
    }

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const layoutShift = entry as LayoutShiftEntry;
        // Only count shifts without recent user input
        if (!layoutShift.hadRecentInput) {
          clsValue.current += layoutShift.value;

          const clsMetric: PerformanceMetric = {
            name: 'CLS',
            value: clsValue.current,
            rating: getRating(clsValue.current, THRESHOLDS.CLS),
            timestamp: Date.now(),
          };

          setMetrics((prev) => ({ ...prev, cls: clsMetric }));
          onMetricCollected?.(clsMetric);
        }
      }
    });

    observer.observe({ type: 'layout-shift', buffered: true });

    return () => observer.disconnect();
  }, [isSupported, onMetricCollected]);

  // FID Observer
  useEffect(() => {
    if (!isSupported) return;
    if (!PerformanceObserver.supportedEntryTypes.includes('first-input')) {
      return;
    }

    const observer = new PerformanceObserver((list) => {
      const entry = list.getEntries()[0] as PerformanceEventTiming;

      if (entry) {
        const fid = entry.processingStart - entry.startTime;

        const fidMetric: PerformanceMetric = {
          name: 'FID',
          value: fid,
          rating: getRating(fid, THRESHOLDS.FID),
          timestamp: Date.now(),
        };

        setMetrics((prev) => ({ ...prev, fid: fidMetric }));
        onMetricCollected?.(fidMetric);
      }
    });

    observer.observe({ type: 'first-input', buffered: true });

    return () => observer.disconnect();
  }, [isSupported, onMetricCollected]);

  // TTFB (Time to First Byte)
  useEffect(() => {
    if (!isSupported) return;

    const navigationEntry = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming;

    if (navigationEntry) {
      const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;

      const ttfbMetric: PerformanceMetric = {
        name: 'TTFB',
        value: ttfb,
        rating: getRating(ttfb, THRESHOLDS.TTFB),
        timestamp: Date.now(),
      };

      setMetrics((prev) => ({ ...prev, ttfb: ttfbMetric }));
      onMetricCollected?.(ttfbMetric);
    }
  }, [isSupported, onMetricCollected]);

  // Manual interaction marking
  const markInteractionStart = useCallback((name: string) => {
    interactionMarks.current.set(name, performance.now());
  }, []);

  const markInteractionEnd = useCallback(
    (name: string): number | null => {
      const startTime = interactionMarks.current.get(name);
      if (startTime === undefined) return null;

      const duration = performance.now() - startTime;
      interactionMarks.current.delete(name);

      if (import.meta.env.DEV && enableWarnings && duration > inpWarningThreshold) {
        console.warn(`[Performance] Slow operation "${name}": ${duration.toFixed(0)}ms`);
      }

      return duration;
    },
    [enableWarnings, inpWarningThreshold]
  );

  // Get performance summary
  const getSummary = useCallback((): string => {
    const { inp, lcp, cls, fid, ttfb } = metrics;

    const lines = ['=== Performance Summary ==='];

    if (inp) lines.push(`INP: ${inp.value.toFixed(0)}ms (${inp.rating})`);
    if (lcp) lines.push(`LCP: ${lcp.value.toFixed(0)}ms (${lcp.rating})`);
    if (cls) lines.push(`CLS: ${cls.value.toFixed(3)} (${cls.rating})`);
    if (fid) lines.push(`FID: ${fid.value.toFixed(0)}ms (${fid.rating})`);
    if (ttfb) lines.push(`TTFB: ${ttfb.value.toFixed(0)}ms (${ttfb.rating})`);

    if (lines.length === 1) {
      lines.push('No metrics collected yet');
    }

    return lines.join('\n');
  }, [metrics]);

  return {
    metrics,
    isSupported,
    markInteractionStart,
    markInteractionEnd,
    getSummary,
  };
}

/**
 * Simple hook for just INP monitoring (lightweight)
 */
export function useINPMonitor(warningThreshold = 200) {
  useEffect(() => {
    if (typeof PerformanceObserver === 'undefined') return;
    if (!PerformanceObserver.supportedEntryTypes?.includes('event')) return;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const eventEntry = entry as PerformanceEventTiming;
        const inp = eventEntry.processingEnd - eventEntry.startTime;

        if (import.meta.env.DEV && inp > warningThreshold) {
          console.warn(
            `[INP] Slow interaction: ${inp.toFixed(0)}ms on ${eventEntry.name}`
          );
        }
      }
    });

    observer.observe({ type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit);

    return () => observer.disconnect();
  }, [warningThreshold]);
}

export default usePerformanceMetrics;
